import { useEffect, useState, type FormEvent } from "react";

import { api, useDatos } from "../api";
import { Alert, Button, Dialog, FormGrid, FormRow, Input, Select, Textarea } from "../ds";
import { navegar } from "../router";
import type { Resultado, Zona } from "../tipos";
import { useAccion } from "./acciones";

/* ── Agregar una inmobiliaria a mano ──────────────────────────────────────────── */

export function DialogoAlta({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const { datos: zonas } = useDatos<Zona[]>(abierto ? "/api/zonas" : null);
  const { pendiente, ejecutar } = useAccion();
  const vacio = { nombre: "", zonaId: "", telefono: "", email: "", sitioWeb: "", instagram: "", notas: "" };
  const [f, setF] = useState(vacio);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (abierto) {
      setF(vacio);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const cambiar = (k: keyof typeof vacio) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    if (f.nombre.trim().length < 2) return setError("Falta el nombre de la inmobiliaria.");
    if (!f.telefono.trim() && !f.email.trim() && !f.instagram.trim()) return setError("Cargá al menos un teléfono, un email o un Instagram: sin contacto queda descartada.");
    setError(null);
    const r = await ejecutar("alta", () => api<{ id: string }>("/api/leads", { metodo: "POST", cuerpo: { ...f, zonaId: f.zonaId || null } }), "Inmobiliaria agregada");
    if (r) {
      onCerrar();
      navegar(`/leads/${r.id}`);
    }
  };

  const opcionesZona = [{ value: "", label: "Sin zona" }, ...(zonas ?? []).map((z) => ({ value: z.id, label: z.nombre, detail: z.partido ?? "CABA" }))];

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title="Agregar inmobiliaria"
      description="Para las que no aparecen en Google Maps: una recomendación, un cartel visto en la calle. Se puntúa al guardarla."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button type="submit" form="form-alta" icon="plus" pending={pendiente === "alta"}>
            Agregar
          </Button>
        </>
      }
    >
      <form id="form-alta" onSubmit={guardar} noValidate>
        <FormGrid>
          <FormRow span="full">
            <Input label="Nombre" required value={f.nombre} onChange={cambiar("nombre")} placeholder="Inmobiliaria del Parque" autoFocus />
          </FormRow>
          <FormRow>
            <Select label="Zona" options={opcionesZona} value={f.zonaId} onChange={cambiar("zonaId")} />
          </FormRow>
          <FormRow>
            <Input label="Teléfono" value={f.telefono} onChange={cambiar("telefono")} placeholder="11 5555-1234" hint="Si es celular, se usa para WhatsApp." inputMode="tel" />
          </FormRow>
          <FormRow>
            <Input label="Email" type="email" value={f.email} onChange={cambiar("email")} placeholder="hola@inmobiliaria.com.ar" />
          </FormRow>
          <FormRow>
            <Input label="Instagram" value={f.instagram} onChange={cambiar("instagram")} placeholder="@inmobiliaria" />
          </FormRow>
          <FormRow span="full">
            <Input label="Sitio web" value={f.sitioWeb} onChange={cambiar("sitioWeb")} placeholder="https://inmobiliaria.com.ar" inputMode="url" />
          </FormRow>
          <FormRow span="full">
            <Textarea label="Notas" value={f.notas} onChange={cambiar("notas")} rows={3} placeholder="De dónde salió, con quién hablar." />
          </FormRow>
          {error ? (
            <FormRow span="full">
              <Alert tone="danger" compact>
                {error}
              </Alert>
            </FormRow>
          ) : null}
        </FormGrid>
      </form>
    </Dialog>
  );
}

/* ── Registrar qué pasó con el contacto ───────────────────────────────────────── */

export const RESULTADOS: { valor: Resultado; etiqueta: string; descripcion: string }[] = [
  { valor: "respondio", etiqueta: "Respondió", descripcion: "Contestó por algún canal. Se frenan los envíos automáticos." },
  { valor: "info_enviada", etiqueta: "Info enviada", descripcion: "Respondió con interés y se le mandó la información de Klavia." },
  { valor: "seguimiento", etiqueta: "Seguimiento", descripcion: "Tiene la info y no avanzó: hay que volver a escribirle." },
  { valor: "pidio_mas", etiqueta: "Le interesó la info", descripcion: "Después de recibir la info pidió que le cuenten más o quiere avanzar." },
  { valor: "reunion", etiqueta: "Reunión agendada", descripcion: "Hay una reunión o demo coordinada." },
  { valor: "cliente", etiqueta: "Es cliente", descripcion: "Ya contrató Klavia." },
  { valor: "no_interesa", etiqueta: "No le interesa", descripcion: "Queda fuera de todo contacto, en todos los canales." },
];

export function DialogoResultado({
  inmobiliaria,
  resultado,
  onCerrar,
  onListo,
}: {
  inmobiliaria: { id: string; nombre: string } | null;
  resultado: Resultado | null;
  onCerrar: () => void;
  onListo?: () => void;
}) {
  const { pendiente, ejecutar } = useAccion();
  const [nota, setNota] = useState("");
  const [cobrado, setCobrado] = useState("");
  const [fecha, setFecha] = useState("");
  useEffect(() => {
    setNota("");
    setCobrado("");
    setFecha("");
  }, [inmobiliaria?.id, resultado]);
  const r = RESULTADOS.find((x) => x.valor === resultado);
  const guardar = async () => {
    if (!inmobiliaria || !resultado) return;
    const ok = await ejecutar(
      "resultado",
      () =>
        api(`/api/leads/${inmobiliaria.id}/resultado`, {
          metodo: "POST",
          cuerpo: {
            resultado,
            nota: nota.trim() || null,
            cobradoUsd: resultado === "cliente" ? cobrado.trim() || undefined : undefined,
            fecha: resultado === "reunion" ? fecha || undefined : undefined,
          },
        }),
      `${inmobiliaria.nombre}: ${r?.etiqueta.toLowerCase()}`,
    );
    if (ok !== undefined) {
      onCerrar();
      onListo?.();
    }
  };
  return (
    <Dialog
      open={Boolean(inmobiliaria && resultado)}
      onClose={onCerrar}
      title={r?.etiqueta ?? ""}
      subtitle={inmobiliaria?.nombre}
      description={r?.descripcion}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button variant={resultado === "no_interesa" ? "danger" : "primary"} pending={pendiente === "resultado"} onClick={guardar}>
            Guardar
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-5)" }}>
        {resultado === "cliente" ? (
          <Input
            label="Cash collected (US$)"
            value={cobrado}
            onChange={(e) => setCobrado(e.target.value)}
            inputMode="decimal"
            placeholder="450"
            hint="Lo que pagó hasta hoy. Se puede corregir después desde la ficha. El tablero lo suma por plantilla."
            autoFocus
          />
        ) : null}
        {resultado === "reunion" ? (
          <Input
            label="Cuándo"
            type="datetime-local"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            hint="Opcional. En el pipeline, cuando la fecha pasa, la reunión pide que cuentes cómo salió."
            autoFocus
          />
        ) : null}
        <Textarea label="Nota" value={nota} onChange={(e) => setNota(e.target.value)} rows={3} placeholder="Qué dijo, cuándo volver a escribir." />
      </div>
    </Dialog>
  );
}

/* ── Descartar con motivo ─────────────────────────────────────────────────────── */

export function DialogoDescarte({
  abierto,
  titulo,
  subtitulo,
  pendiente,
  onCerrar,
  onConfirmar,
}: {
  abierto: boolean;
  titulo: string;
  subtitulo?: string;
  pendiente: boolean;
  onCerrar: () => void;
  onConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState("");
  useEffect(() => setMotivo(""), [abierto]);
  const MOTIVOS = ["Ya trabaja con otra empresa", "No hace carteles", "Es una franquicia grande", "Datos equivocados"];
  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title={titulo}
      subtitle={subtitulo}
      description="Sale de la cola y de las secuencias. Queda en la lista con su motivo, por si cambia de idea."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button variant="danger" pending={pendiente} onClick={() => onConfirmar(motivo.trim() || "sin motivo")}>
            Descartar
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-5)" }}>
        <Input label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Por qué no es para Klavia" autoFocus />
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--s-3)" }}>
          {MOTIVOS.map((m) => (
            <Button key={m} size="sm" variant="outline" onClick={() => setMotivo(m)}>
              {m}
            </Button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
